import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let id = localStorage.getItem("pushapp_chat_session");
  if (!id) {
    id = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    localStorage.setItem("pushapp_chat_session", id);
  }
  return id;
};

const SUGGESTIONS = [
  "Che notifica invio questa settimana?",
  "Come creo un evento?",
  "Quante notifiche a settimana?",
  "Idee per il mio negozio",
];

const WELCOME = "Ciao! 👋 Sono Asia, l'assistente di PushApp. Posso aiutarti a usare la tua app: come creare promozioni ed eventi, quali notifiche inviare, le strategie del mese. Cosa ti serve?";

export const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading, open]);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-asia", handler);
    return () => window.removeEventListener("open-asia", handler);
  }, []);

  const sendMessage = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }]);
    setLoading(true);

    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: content }),
      });

      if (!res.ok || !res.body) throw new Error("network");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let assistantText = "";
      let added = false;
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          try {
            const data = JSON.parse(payload);
            if (data.delta) {
              assistantText += data.delta;
              if (!added) {
                setMessages((m) => [...m, { role: "assistant", content: assistantText }]);
                added = true;
              } else {
                setMessages((m) => {
                  const copy = [...m];
                  copy[copy.length - 1] = { role: "assistant", content: assistantText };
                  return copy;
                });
              }
              setLoading(false);
            } else if (data.error) {
              setMessages((m) => [...m, { role: "assistant", content: data.error }]);
            }
          } catch (_) {}
        }
      }
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Connessione non riuscita. Riprova tra poco o scrivici a info@pushapp.it." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      <div className="fixed bottom-24 right-6 z-50">
        {open ? (
          <button
            onClick={() => setOpen(false)}
            data-testid="chatbot-toggle"
            aria-label="Chiudi assistente"
            className="ml-auto flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-brand to-brand-green text-white shadow-2xl shadow-brand/40 transition-transform duration-300 hover:scale-105"
          >
            <X size={24} />
          </button>
        ) : (
          <button
            onClick={() => setOpen(true)}
            data-testid="chatbot-toggle"
            aria-label="Chiedi ad Asia"
            className="group flex items-center gap-2.5 rounded-full bg-gradient-to-br from-brand to-brand-green py-3 pl-3.5 pr-5 text-white shadow-2xl shadow-brand/40 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-brand/50"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20">
              <MessageCircle size={20} strokeWidth={2.5} />
            </span>
            <span className="font-heading font-bold text-sm whitespace-nowrap">Chiedi ad Asia</span>
          </button>
        )}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-44 right-6 z-50 w-[calc(100vw-3rem)] sm:w-[400px] h-[560px] max-h-[70vh] rounded-3xl bg-white shadow-2xl border border-gray-100 flex flex-col overflow-hidden"
            data-testid="chatbot-window"
          >
            {/* Header */}
            <div className="bg-gradient-to-br from-brand to-brand-green px-5 py-4 flex items-center gap-3 text-white">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/20">
                <Sparkles size={22} strokeWidth={2.5} />
              </span>
              <div>
                <p className="font-heading font-bold leading-tight">Asia · Assistente PushApp</p>
                <p className="text-xs text-white/80 flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-brand-green ring-2 ring-white/40" /> Online ora
                </p>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#F9FAFB]">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-message-${m.role}`}>
                  <div
                    className={`max-w-[82%] rounded-2xl px-4 py-2.5 font-body text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-brand text-white rounded-br-md"
                        : "bg-white text-ink border border-gray-100 rounded-bl-md shadow-sm"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className="rounded-2xl rounded-bl-md bg-white border border-gray-100 px-4 py-3 shadow-sm flex items-center gap-1">
                    <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
                  </div>
                </div>
              )}

              {messages.length <= 1 && !loading && (
                <div className="pt-2 flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => sendMessage(s)}
                      data-testid="chat-suggestion"
                      className="rounded-full border border-brand/30 bg-white px-3 py-1.5 text-xs font-medium text-brand hover:bg-brand hover:text-white transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
              className="border-t border-gray-100 p-3 flex items-center gap-2 bg-white"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Scrivi un messaggio..."
                data-testid="chat-input"
                className="flex-1 rounded-full bg-[#F9FAFB] border border-gray-200 px-4 py-3 font-body text-sm text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                data-testid="chat-send-button"
                aria-label="Invia"
                className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-brand text-white transition-all hover:bg-brand-hover disabled:opacity-50"
              >
                <Send size={18} strokeWidth={2.5} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
